// src/components/SessionControlScreen.jsx
// Controle de sessão premium: registra sessão, normaliza dados e gera insight com IA.
// Insight Premium: POST /psy/insight/session
// Entrada esperada:
// navigate("/session-control", { state: { appointment } })
// O backend finaliza o agendamento vinculado e sincroniza o Google Calendar.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  BrainCircuit,
  CalendarCheck2,
  CalendarOff,
  CircleAlert,
  CircleX,
  ClipboardCheck,
  Clock3,
  LoaderCircle,
  LockKeyhole,
  MapPin,
  Menu,
  NotebookTabs,
  RefreshCw,
  Save,
  ShieldAlert,
  Sparkles,
  Video,
} from "lucide-react";

import AppSidebar from "../components/AppSidebar";
import { useToast } from "../context/ToastContext";
import Logo from "../assets/logoClaro.webp";

import "../sass/pages/sessionControl.scss";

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_NAME: "psy_usuario",
  PLAN: "plano",
  PLAN_VALUE: "valorPlano",
  STATUS_ASSINATURA: "statusAssinatura",
  ASSINATURA_ATIVA: "assinaturaAtiva",
  AVISO_ASSINATURA: "avisoAssinatura",
  RECURSOS: "recursos",
};

const SESSION_STATUS_OPTIONS = [
  {
    value: "concluida",
    label: "Concluída",
    description: "Atendimento realizado normalmente.",
    icon: CalendarCheck2,
  },
  {
    value: "cancelada",
    label: "Cancelada",
    description: "Sessão não realizada ou interrompida.",
    icon: CircleX,
  },
];

const readAuth = (key) =>
  sessionStorage.getItem(key) || localStorage.getItem(key) || "";

const saveAuthToken = (token) => {
  if (!token) return;
  localStorage.setItem(AUTH_KEYS.TOKEN, token);
  sessionStorage.setItem(AUTH_KEYS.TOKEN, token);
};

async function readErrorMessage(response) {
  try {
    const contentType = response.headers.get("content-type") || "";

    if (contentType.includes("application/json")) {
      const json = await response.json();
      return json?.message || json?.detail || json?.error || JSON.stringify(json);
    }

    const text = await response.text();
    return text || `HTTP ${response.status}`;
  } catch {
    return `HTTP ${response.status}`;
  }
}

function normalizeCommunicationText(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

const CHANNEL_FAILURE_STATUS = new Set([
  "failed",
  "undelivered",
  "canceled",
  "cancelled",
  "error",
  "erro",
  "falhou",
]);

const CHANNEL_SUCCESS_STATUS = new Set([
  "created",
  "queued",
  "sent",
  "delivered",
  "read",
  "success",
  "sucesso",
  "ok",
  "enviado",
  "entregue",
  "lido",
  "encaminhado",
]);

function normalizeChannelKey(value = "") {
  const normalized = normalizeCommunicationText(value).replace(/[^a-z]/g, "");

  if (normalized.includes("whatsapp")) return "whatsapp";
  if (normalized.includes("email") || normalized.includes("mail")) return "email";

  return normalized;
}

function findChannelResult(payload, channel) {
  if (!payload || typeof payload !== "object") return null;

  const target = normalizeChannelKey(channel);

  const containers = [
    payload,
    payload?.resultados,
    payload?.resultado,
    payload?.canais,
    payload?.comunicacao,
    payload?.comunicacoes,
    payload?.envios,
    payload?.termo,
    payload?.lembrete,
    payload?.cobranca,
    payload?.recibo,
    payload?.termo?.resultados,
    payload?.lembrete?.resultados,
    payload?.cobranca?.resultados,
    payload?.recibo?.resultados,
    payload?.recibo?.comunicacao,
    payload?.recibo?.comunicacoes,
  ].filter(Boolean);

  for (const container of containers) {
    if (Array.isArray(container)) {
      for (const item of container) {
        if (!item || typeof item !== "object") continue;

        const nestedDirect =
          target === "whatsapp"
            ? item.whatsapp ||
              item.whatsApp ||
              item.resultadoWhatsapp ||
              item.whatsappResultado
            : item.email ||
              item.eMail ||
              item.resultadoEmail ||
              item.emailResultado;

        if (
          nestedDirect !== undefined &&
          nestedDirect !== null &&
          (typeof nestedDirect === "object" ||
            typeof nestedDirect === "boolean" ||
            CHANNEL_SUCCESS_STATUS.has(
              normalizeCommunicationText(nestedDirect).replace(/\s+/g, "_")
            ) ||
            CHANNEL_FAILURE_STATUS.has(
              normalizeCommunicationText(nestedDirect).replace(/\s+/g, "_")
            ))
        ) {
          return nestedDirect;
        }

        const key = normalizeChannelKey(
          item?.canal ||
            item?.channel ||
            item?.tipoCanal ||
            item?.tipo ||
            item?.nome
        );

        if (key === target) return item;
      }

      continue;
    }

    if (typeof container !== "object") continue;

    const direct =
      target === "whatsapp"
        ? container.whatsapp ||
          container.whatsApp ||
          container.resultadoWhatsapp ||
          container.whatsappResultado
        : container.email ||
          container.eMail ||
          container.resultadoEmail ||
          container.emailResultado;

    if (direct !== undefined && direct !== null) {
      return direct;
    }

    const ownKey = normalizeChannelKey(
      container?.canal ||
        container?.channel ||
        container?.tipoCanal
    );

    if (ownKey === target) {
      return container;
    }
  }

  return null;
}

function normalizeChannelResult(payload, channel) {
  const raw = findChannelResult(payload, channel);

  if (raw === null || raw === undefined) return null;

  if (typeof raw === "boolean") {
    return {
      channel,
      success: raw,
      failed: !raw,
      status: raw ? "sent" : "failed",
      error: "",
    };
  }

  if (typeof raw === "string") {
    const status = normalizeCommunicationText(raw).replace(/\s+/g, "_");

    return {
      channel,
      success: CHANNEL_SUCCESS_STATUS.has(status),
      failed: CHANNEL_FAILURE_STATUS.has(status),
      status,
      error: "",
    };
  }

  if (typeof raw !== "object") return null;

  const status = normalizeCommunicationText(
    raw.status ||
      raw.messageStatus ||
      raw.twilioStatus ||
      raw.situacao ||
      ""
  ).replace(/\s+/g, "_");

  const successFlag =
    raw.success ??
    raw.sucesso ??
    raw.ok ??
    raw.enviado ??
    raw.sent ??
    null;

  const requested =
    raw.solicitado ??
    raw.requested ??
    raw.processado ??
    true;

  const failed =
    requested !== false &&
    (successFlag === false || CHANNEL_FAILURE_STATUS.has(status));

  const success =
    requested !== false &&
    !failed &&
    (successFlag === true || CHANNEL_SUCCESS_STATUS.has(status));

  return {
    channel,
    success,
    failed,
    status,
    requested,
    error:
      raw.erroMensagem ||
      raw.erro ||
      raw.error ||
      raw.message ||
      "",
  };
}

function getChannelStatusLabel(result) {
  if (!result) return "";

  if (result.requested === false) return "Não solicitado";

  const status = normalizeCommunicationText(result.status).replace(/\s+/g, "_");

  if (result.failed) return "Falhou";

  if (result.channel === "whatsapp") {
    if (status === "read" || status === "lido") return "Lido";
    if (status === "delivered" || status === "entregue") return "Entregue";
    if (status === "sent" || status === "enviado") return "Enviado";
    if (
      status === "queued" ||
      status === "created" ||
      status === "encaminhado"
    ) {
      return "Encaminhado";
    }

    if (result.success) return "Encaminhado";
    return "Processando";
  }

  if (result.success) return "Enviado";
  if (result.failed) return "Falhou";

  return "Processando";
}

function buildCommunicationFeedback(payload, fallbackMessage = "") {
  const email = normalizeChannelResult(payload, "email");
  const whatsapp = normalizeChannelResult(payload, "whatsapp");
  const results = [email, whatsapp].filter(Boolean);

  if (!results.length) {
    return {
      hasChannels: false,
      tone: "success",
      message: fallbackMessage,
      details: [],
    };
  }

  const details = results.map((result) => {
    const label = result.channel === "whatsapp" ? "WhatsApp" : "E-mail";
    const statusLabel = getChannelStatusLabel(result);

    if (result.failed && result.error) {
      return `${label}: ${statusLabel} — ${result.error}`;
    }

    return `${label}: ${statusLabel}`;
  });

  const hasSuccess = results.some((item) => item.success);
  const hasFailure = results.some((item) => item.failed);

  return {
    hasChannels: true,
    tone: hasFailure ? (hasSuccess ? "warning" : "error") : "success",
    message: details.join(" • "),
    details,
  };
}


function normalizeText(value = "") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function normalizeSpaces(value = "") {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function parseJsonStorage(value, fallback = {}) {
  try {
    if (!value) return fallback;
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function isPremiumByPlan(plano) {
  const value = normalizeText(plano);

  return (
    value === "premium" ||
    value === "pro" ||
    value === "psyassist premium" ||
    value.includes("premium")
  );
}

function isStatusLiberado(statusAssinatura) {
  const status = normalizeText(statusAssinatura || "active");
  return ["active", "trialing", "past_due"].includes(status);
}

function isAssinaturaAtiva(value) {
  if (value === "" || value === null || value === undefined) return true;
  return String(value).trim().toLowerCase() !== "false";
}

function getStoredRecursos() {
  return parseJsonStorage(readAuth(AUTH_KEYS.RECURSOS), {});
}

function canUseFeature(feature) {
  const recursos = getStoredRecursos();

  if (recursos?.[feature] === true) return true;

  if (feature === "ia" && recursos?.insightsIa === true) return true;
  if (feature === "insightsIa" && recursos?.ia === true) return true;
  if (feature === "cobrancas" && recursos?.cobrancas === true) return true;
  if (feature === "relatoriosAvancados" && recursos?.relatoriosAvancados === true) return true;

  return (
    isPremiumByPlan(readAuth(AUTH_KEYS.PLAN)) &&
    isStatusLiberado(readAuth(AUTH_KEYS.STATUS_ASSINATURA)) &&
    isAssinaturaAtiva(readAuth(AUTH_KEYS.ASSINATURA_ATIVA))
  );
}


function normalizeMultiline(value = "") {
  return String(value || "")
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((line) => line.replace(/[ \t]+/g, " ").trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function onlyDigits(value = "") {
  return String(value || "").replace(/\D/g, "");
}

function parseMoneyBR(value) {
  if (value === null || value === undefined || value === "") return 0;

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }

  const normalized = String(value)
    .replace(/[R$\s]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");

  const parsed = Number(normalized);

  return Number.isFinite(parsed) ? parsed : 0;
}

function formatMoneyBR(value) {
  const number = parseMoneyBR(value);

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(number);
}

function formatMoneyInput(value) {
  const digits = onlyDigits(value).slice(0, 10);
  const cents = Number(digits || "0") / 100;

  if (!digits) return "";

  return cents.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDateTimeBR(dateLike) {
  try {
    const date = new Date(dateLike);

    if (Number.isNaN(date.getTime())) return "Não informado";

    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  } catch {
    return "Não informado";
  }
}

// function formatTimeBR(dateLike) {
//   try {
//     const date = new Date(dateLike);

//     if (Number.isNaN(date.getTime())) return "--:--";

//     return new Intl.DateTimeFormat("pt-BR", {
//       hour: "2-digit",
//       minute: "2-digit",
//     }).format(date);
//   } catch {
//     return "--:--";
//   }
// }

function dateToInputValue(dateLike) {
  const date = new Date(dateLike);

  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function timeToInputValue(dateLike) {
  const date = new Date(dateLike);

  if (Number.isNaN(date.getTime())) return "";

  const hour = String(date.getHours()).padStart(2, "0");
  const minute = String(date.getMinutes()).padStart(2, "0");

  return `${hour}:${minute}`;
}

function createLocalISO(dateValue, timeValue) {
  const dateMatch = String(dateValue || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const timeMatch = String(timeValue || "").match(/^(\d{2}):(\d{2})$/);

  if (!dateMatch || !timeMatch) return null;

  const year = Number(dateMatch[1]);
  const month = Number(dateMatch[2]);
  const day = Number(dateMatch[3]);
  const hour = Number(timeMatch[1]);
  const minute = Number(timeMatch[2]);

  const date = new Date(year, month - 1, day, hour, minute, 0, 0);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day ||
    date.getHours() !== hour ||
    date.getMinutes() !== minute
  ) {
    return null;
  }

  return date.toISOString();
}

function getWordCount(text) {
  return normalizeSpaces(text).split(" ").filter(Boolean).length;
}

function getAppointmentClient(appointment) {
  if (!appointment) return {};

  if (appointment?.clienteId && typeof appointment.clienteId === "object") {
    return appointment.clienteId;
  }

  if (appointment?.cliente && typeof appointment.cliente === "object") {
    return appointment.cliente;
  }

  return {
    _id: appointment?.clienteId || appointment?.clientId || "",
    nome: appointment?.nomeCliente || "Cliente",
  };
}

function getClientId(appointment) {
  const cliente = getAppointmentClient(appointment);

  return (
    cliente?._id ||
    appointment?.clienteId?._id ||
    appointment?.clienteId ||
    appointment?.clientId ||
    ""
  );
}

function getSessionMode(appointment) {
  const service = String(appointment?.servico || appointment?.tipoSessao || "")
    .toLowerCase()
    .trim();

  if (service.includes("online")) {
    return {
      label: "Online",
      icon: Video,
      tone: "blue",
    };
  }

  return {
    label: "Presencial",
    icon: MapPin,
    tone: "green",
  };
}

function getInitials(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return "C";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function getGoogleCalendarInfo(appointment) {
  const googleCalendar = appointment?.googleCalendar || {};

  const eventId = String(
    googleCalendar?.eventId ||
      appointment?.googleEventId ||
      ""
  ).trim();

  const sincronizado =
    googleCalendar?.sincronizado === true ||
    Boolean(eventId);

  const ultimoErro = String(
    googleCalendar?.ultimoErro || ""
  ).trim();

  if (eventId && sincronizado) {
    return {
      linked: true,
      status: "linked",
      icon: CalendarCheck2,
      title: "Vinculado ao Google Calendar",
      description:
        "Ao concluir ou cancelar, este compromisso será atualizado automaticamente no Google.",
    };
  }

  if (ultimoErro) {
    return {
      linked: false,
      status: "error",
      icon: CircleAlert,
      title: "Falha na sincronização com o Google",
      description:
        "O agendamento está salvo no PsyAssist, mas a sincronização do calendário precisa ser revisada.",
    };
  }

  if (
    googleCalendar?.ultimaTentativaEm ||
    googleCalendar?.sincronizado === false
  ) {
    return {
      linked: false,
      status: "pending",
      icon: RefreshCw,
      title: "Sincronização com o Google pendente",
      description:
        "O agendamento será mantido no PsyAssist e uma nova sincronização poderá ser realizada.",
    };
  }

  return {
    linked: false,
    status: "off",
    icon: CalendarOff,
    title: "Sem vínculo com o Google Calendar",
    description:
      "A finalização afetará apenas o agendamento armazenado no PsyAssist.",
  };
}

function buildSessionDetails({
  appointment,
  cliente,
  dataSessao,
  sessionMode,
  status,
  duracao,
  pago,
  valor,
  resumo,
}) {
  return [
    `Paciente: ${cliente?.nome || "Cliente não informado"}`,
    `Data/hora da sessão: ${formatDateTimeBR(dataSessao)}`,
    `Tipo de sessão: ${sessionMode?.label || "Não informado"}`,
    `Status informado: ${status}`,
    `Duração: ${Number(onlyDigits(duracao)) || 0} minuto(s)`,
    `Pagamento efetuado: ${pago ? "sim" : "não"}`,
    `Valor: ${formatMoneyBR(valor)}`,
    `Serviço/agendamento: ${appointment?.servico || appointment?.tipoSessao || "Não informado"}`,
    "",
    "Resumo registrado pelo psicólogo:",
    normalizeMultiline(resumo),
  ].join("\n");
}

function Field({ label, required, error, hint, children, className = "" }) {
  return (
    <div className={`sc-field ${className} ${error ? "sc-field--error" : ""}`}>
      <div className="sc-field__top">
        <label className="sc-field__label">
          {label}
          {required ? <span className="sc-req">*</span> : null}
        </label>

        {hint ? <span className="sc-field__hint">{hint}</span> : null}
      </div>

      {children}

      {error ? (
        <div className="sc-field__err" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}

function StatusOptionCard({ option, active, disabled, onSelect }) {
  const Icon = option.icon;

  return (
    <button
      className={`sc-status-card ${active ? "sc-status-card--active" : ""}`}
      type="button"
      onClick={() => onSelect(option.value)}
      disabled={disabled}
      aria-pressed={active}
    >
      <span className="sc-status-card__icon" aria-hidden="true">
        <Icon size={19} />
      </span>
      <span>
        <strong>{option.label}</strong>
        <small>{option.description}</small>
      </span>
    </button>
  );
}

function PaymentSwitch({ checked, onChange, disabled }) {
  return (
    <button
      className={`sc-pay-switch ${checked ? "sc-pay-switch--active" : ""}`}
      type="button"
      onClick={() => onChange(!checked)}
      disabled={disabled}
      aria-pressed={checked}
    >
      <span className="sc-pay-switch__visual" aria-hidden="true">
        <span />
      </span>

      <span className="sc-pay-switch__text">
        <strong>Pagamento efetuado</strong>
        <small>
          {checked
            ? "A sessão será registrada como paga."
            : "A sessão será registrada com pagamento pendente."}
        </small>
      </span>
    </button>
  );
}

export default function SessionControlScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const appointment = location.state?.appointment || null;
  const cliente = useMemo(() => getAppointmentClient(appointment), [appointment]);
  const clienteId = useMemo(() => getClientId(appointment), [appointment]);
  const sessionMode = useMemo(() => getSessionMode(appointment), [appointment]);
  const googleCalendarInfo = useMemo(
    () => getGoogleCalendarInfo(appointment),
    [appointment]
  );

  const SessionModeIcon = sessionMode.icon;
  const GoogleStatusIcon = googleCalendarInfo.icon;

  const dataOriginalSessao = appointment?.dataHora || appointment?.data || "";
  const isBaixado = appointment?.status === "baixado";

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dataSessao, setDataSessao] = useState(() =>
    dateToInputValue(dataOriginalSessao)
  );
  const [horaSessao, setHoraSessao] = useState(() =>
    timeToInputValue(dataOriginalSessao)
  );
  const [status, setStatus] = useState("concluida");
  const [resumo, setResumo] = useState("");
  const [pago, setPago] = useState(true);
  const [duracao, setDuracao] = useState("50");
  const [valor, setValor] = useState(() =>
    appointment?.valorAgendamento || appointment?.valor
      ? formatMoneyInput(String(Math.round(parseMoneyBR(appointment?.valorAgendamento ?? appointment?.valor) * 100)))
      : "0,00"
  );
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const [sessionInsight, setSessionInsight] = useState("");
  const [insightLoading, setInsightLoading] = useState(false);
  const [insightError, setInsightError] = useState("");

  const nomeUsuario = useMemo(() => readAuth(AUTH_KEYS.USER_NAME) || "", []);

  const returnTo = useMemo(() => {
    const candidate =
      String(
        location.state?.returnTo ||
          ""
      ).trim();

    return candidate.startsWith("/")
      ? candidate
      : "/schedule";
  }, [location.state?.returnTo]);

  const isPremium = useMemo(() => canUseFeature("ia") || canUseFeature("insightsIa"), []);

  const resumoWords = useMemo(() => getWordCount(resumo), [resumo]);

  const completion = useMemo(() => {
    const checks = [
      Boolean(appointment?._id),
      Boolean(clienteId),
      Boolean(dataSessao),
      Boolean(horaSessao),
      Boolean(status),
      Number(onlyDigits(duracao)) > 0,
      normalizeSpaces(resumo).length >= 10,
    ];

    return Math.round((checks.filter(Boolean).length / checks.length) * 100);
  }, [appointment, clienteId, dataSessao, horaSessao, status, duracao, resumo]);

  const canGenerateInsight = useMemo(() => {
    return (
      Boolean(appointment?._id) &&
      Boolean(clienteId) &&
      Boolean(dataSessao) &&
      Boolean(horaSessao) &&
      normalizeSpaces(resumo).length >= 10
    );
  }, [appointment, clienteId, dataSessao, horaSessao, resumo]);

  const showMessage = useCallback(
    (type, text, options = {}) => {
      const tone = ["success", "warning", "error", "info"].includes(type)
        ? type
        : "info";

      const defaultTitles = {
        success: "Operação concluída",
        warning: "Atenção ao registro",
        error: "Não foi possível concluir",
        info: "Informação",
      };

      toast[tone]({
        title: options.title || defaultTitles[tone],
        message: text || "",
        details: Array.isArray(options.details) ? options.details : [],
        actionLabel: options.actionLabel || "",
        onAction: typeof options.onAction === "function" ? options.onAction : null,
        duration:
          options.duration !== undefined
            ? options.duration
            : options.actionLabel
              ? 0
              : 5600,
      });
    },
    [toast]
  );

  useEffect(() => {
    if (!appointment) {
      navigate("/schedule", { replace: true });
    }
  }, [appointment, navigate]);


  if (!appointment) {
    return null;
  }

  const authOrRedirect = () => {
    const token = readAuth(AUTH_KEYS.TOKEN);

    if (!token) {
      showMessage("error", "Sessão expirada. Faça login novamente.", {
        title: "Sessão encerrada",
        duration: 6500,
      });
      navigate("/login", { replace: true });
      return null;
    }

    return { token };
  };

  const setResumoSafe = (value) => {
    setResumo(value);
    setErrors((prev) => ({ ...prev, resumo: "" }));
    setInsightError("");
  };

  const setDuracaoSafe = (value) => {
    const digits = onlyDigits(value).slice(0, 3);
    setDuracao(digits);
    setErrors((prev) => ({ ...prev, duracao: "" }));
  };

  const setValorSafe = (value) => {
    setValor(formatMoneyInput(value));
    setErrors((prev) => ({ ...prev, valor: "" }));
  };

  const validate = () => {
    const nextErrors = {};

    if (isBaixado) {
      nextErrors.geral = "Este agendamento já foi encerrado.";
    }

    if (!appointment?._id) {
      nextErrors.geral = "Agendamento não identificado.";
    }

    if (!clienteId) {
      nextErrors.geral = "Cliente não identificado no agendamento.";
    }

    const dataHoraSessao = createLocalISO(dataSessao, horaSessao);

    if (!dataSessao || !horaSessao || !dataHoraSessao) {
      nextErrors.dataSessao = "Informe uma data e um horário válidos.";
    } else if (new Date(dataHoraSessao).getTime() > Date.now() + 5 * 60 * 1000) {
      nextErrors.dataSessao = "A sessão não pode ser finalizada com data futura.";
    }

    if (!["concluida", "cancelada"].includes(status)) {
      nextErrors.status = "Selecione um status válido.";
    }

    const duracaoNumber = Number(onlyDigits(duracao));

    if (!duracaoNumber || duracaoNumber <= 0) {
      nextErrors.duracao = "Informe a duração em minutos.";
    } else if (duracaoNumber > 600) {
      nextErrors.duracao = "A duração não pode ultrapassar 600 minutos.";
    }

    if (!normalizeSpaces(resumo)) {
      nextErrors.resumo = "Informe o resumo da sessão.";
    } else if (normalizeSpaces(resumo).length < 10) {
      nextErrors.resumo = "O resumo precisa ter pelo menos 10 caracteres.";
    }

    const valorNumber = parseMoneyBR(valor);

    if (valorNumber < 0) {
      nextErrors.valor = "Valor inválido.";
    } else if (pago && valorNumber <= 0) {
      nextErrors.valor = "Informe um valor maior que zero para registrar o pagamento.";
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      showMessage("error", nextErrors.geral || "Revise os campos destacados.");
      return false;
    }

    return true;
  };

  const buildPayload = () => {
    return {
      agendamentoId: appointment?._id,
      clienteId,
      dataSessao: createLocalISO(dataSessao, horaSessao),
      duracao: Number(onlyDigits(duracao)) || 50,
      status,
      resumo: normalizeMultiline(resumo),
      pagamentoEfetuado: Boolean(pago),
      tipoSessao: sessionMode.label,
      valor: parseMoneyBR(valor),
    };
  };

  const gerarInsightSessao = async () => {
    setInsightError("");

    if (!isPremium) {
      setInsightError("Recurso disponível apenas para usuários do plano Premium.");
      return;
    }

    if (!canGenerateInsight) {
      setInsightError("Informe pelo menos um resumo da sessão com 10 caracteres para gerar o insight.");
      return;
    }

    const auth = authOrRedirect();
    if (!auth) return;

    setInsightLoading(true);

    try {
      const sessionDetails = buildSessionDetails({
        appointment,
        cliente,
        dataSessao: createLocalISO(dataSessao, horaSessao),
        sessionMode,
        status,
        duracao,
        pago,
        valor,
        resumo,
      });

      const response = await fetch(
        `${API_BASE}/psy/insight/session`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${auth.token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            sessionDetails,
          }),
        }
      );

      saveAuthToken(response.headers.get("x-new-token"));

      if (response.status === 401) {
        showMessage("error", "Sessão expirada. Faça login novamente.", {
          title: "Sessão encerrada",
          duration: 6500,
        });
        navigate("/login", { replace: true });
        return;
      }

      if (response.status === 403) {
        const message = await readErrorMessage(response);
        setInsightError(message || "Recurso disponível apenas para usuários do plano Premium.");
        setSessionInsight("");
        return;
      }

      if (!response.ok) {
        const message = await readErrorMessage(response);
        setInsightError(message || "Não foi possível gerar o insight da sessão.");
        setSessionInsight("");
        return;
      }

      const data = await response.json();
      const insight = String(data?.insight || "").trim();

      setSessionInsight(insight || "Insight não gerado.");
    } catch (error) {
      console.error("Erro ao gerar insight da sessão:", error);
      setInsightError("Falha de comunicação ao gerar insight da sessão.");
      setSessionInsight("");
    } finally {
      setInsightLoading(false);
    }
  };

  const salvarSessao = async () => {

    if (!validate()) return;

    const auth = authOrRedirect();
    if (!auth) return;

    setSaving(true);

    const payload = buildPayload();

    try {
      const sessionResponse = await fetch(
        `${API_BASE}/psy/session`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${auth.token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      saveAuthToken(sessionResponse.headers.get("x-new-token"));

      if (sessionResponse.status === 401 || sessionResponse.status === 403) {
        showMessage("error", "Sessão expirada. Faça login novamente.", {
          title: "Sessão encerrada",
          duration: 6500,
        });
        navigate("/login", { replace: true });
        return;
      }

      const sessionData = await sessionResponse.json().catch(() => null);

      if (!sessionResponse.ok) {
        showMessage(
          "error",
          sessionData?.message ||
            `Erro ao registrar sessão (${sessionResponse.status}).`
        );
        return;
      }

      if (
        sessionData?.agendamentoAtualizado === false &&
        sessionData?.atualizacaoAgendamento?.ignorado !== true
      ) {
        showMessage(
          "warning",
          sessionData?.message ||
            "Sessão salva, mas o agendamento vinculado precisa ser revisado.",
          {
            title: "Sessão salva com atenção",
            duration: 7000,
          }
        );
      }

      if (
        sessionData?.prontuarioAtualizado === false
      ) {
        showMessage(
          "warning",
          sessionData?.prontuarioErro ||
            "A sessão foi registrada, mas não foi possível alimentar o prontuário automaticamente.",
          {
            title:
              "Prontuário precisa de atenção",
            duration:
              8000,
          }
        );
      }

      const googleFoiSincronizado =
        sessionData?.sincronizacaoGoogle?.success === true ||
        sessionData?.sincronizacaoGoogle?.sincronizado === true ||
        sessionData?.atualizacaoAgendamento?.sincronizacaoGoogle?.success === true ||
        sessionData?.atualizacaoAgendamento?.sincronizacaoGoogle?.sincronizado === true;

      const mensagemBase =
        sessionData?.pagamentoEfetuado
          ? "Sessão registrada e receita criada com sucesso."
          : sessionData?.pendenciaCobrancaCriada
            ? "Sessão registrada e adicionada às cobranças."
            : sessionData?.prontuarioAtualizado === true
              ? "Sessão registrada e prontuário atualizado com sucesso."
              : "Sessão registrada com sucesso.";

      const mensagemSessao =
        googleCalendarInfo.linked && googleFoiSincronizado
          ? `${mensagemBase} Google Calendar atualizado.`
          : mensagemBase;

      const reciboFeedback = sessionData?.pagamentoEfetuado
        ? buildCommunicationFeedback(
            sessionData?.recibo || sessionData,
            ""
          )
        : {
            hasChannels: false,
            tone: "success",
            message: "",
            details: [],
          };

      showMessage(
        reciboFeedback.hasChannels ? reciboFeedback.tone : "success",
        reciboFeedback.hasChannels
          ? `${mensagemSessao} Recibo — ${reciboFeedback.message}`
          : mensagemSessao,
        {
          title:
            reciboFeedback.hasChannels && reciboFeedback.tone === "warning"
              ? "Sessão registrada com atenção"
              : "Sessão registrada",
          details: reciboFeedback.hasChannels
            ? reciboFeedback.details
            : [],
        }
      );

      navigate(
        returnTo,
        {
          replace: true,
          state: {
            refresh: true,
          },
        }
      );
    } catch (error) {
      console.error("Erro ao registrar sessão:", error);
      showMessage("error", "Falha de comunicação com a API.");
    } finally {
      setSaving(false);
    }
  };

  const voltar = () => {
    if (saving) return;

    navigate(
      returnTo,
      {
        state: {
          refresh: true,
        },
      }
    );
  };

  return (
    <div className="sc-page">
      <div className="sc-bg" aria-hidden="true">
        <span className="sc-bg__orb sc-bg__orb--one" />
        <span className="sc-bg__orb sc-bg__orb--two" />
        <span className="sc-bg__orb sc-bg__orb--three" />
      </div>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="sc-topbar">
        <button
          className="sc-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          <Menu size={19} aria-hidden="true" />
        </button>

        <div className="sc-brand">
          <img src={Logo} alt="PsyAssist" className="sc-brand__logo" />
          <div className="sc-brand__text">
            <strong>Registrar sessão</strong>
            <span>Agendamentos • {nomeUsuario || "Usuário"}</span>
          </div>
        </div>

        <div className="sc-top-actions">
          <button
            className="sc-btn sc-btn--ghost sc-btn--desktop"
            type="button"
            onClick={voltar}
            disabled={saving}
          >
            <ArrowLeft size={16} aria-hidden="true" />
            Voltar
          </button>

          <button
            className="sc-btn sc-btn--primary"
            type="button"
            onClick={salvarSessao}
            disabled={saving || isBaixado}
          >
            {saving ? (
              <>
                <LoaderCircle size={16} aria-hidden="true" className="sc-icon-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save size={16} aria-hidden="true" />
                Registrar
              </>
            )}
          </button>
        </div>
      </header>

      <main className="sc-main">
        <section className="sc-hero">
          <div>
            <span className="sc-kicker">
              <ClipboardCheck size={15} aria-hidden="true" />
              Controle de atendimento
            </span>
            <h1>Registrar sessão</h1>
            <p>
              Finalize o atendimento e registre o resumo clínico. Em sessões
              concluídas, esse conteúdo alimenta automaticamente uma nova
              evolução no prontuário do paciente.
            </p>
          </div>

          <aside className="sc-patient-card">
            <div className={`sc-patient-card__avatar sc-patient-card__avatar--${sessionMode.tone}`}>
              {getInitials(cliente?.nome || "Cliente")}
            </div>

            <div className="sc-patient-card__content">
              <small>Paciente</small>
              <strong>{cliente?.nome || "Cliente"}</strong>
              <span>
                <SessionModeIcon size={15} aria-hidden="true" />
                {sessionMode.label}
                <b aria-hidden="true">•</b>
                <Clock3 size={14} aria-hidden="true" />
                {horaSessao || "--:--"}
              </span>
            </div>
          </aside>
        </section>

        <section className="sc-wrap">
          {isBaixado ? (
            <div className="sc-banner sc-banner--warn">
              <span><ShieldAlert size={17} aria-hidden="true" /></span>
              <strong>Este agendamento já foi encerrado.</strong>
            </div>
          ) : null}


          <section className="sc-card">
            <div className="sc-card__head">
              <div>
                <span className="sc-card__eyebrow">
                  <NotebookTabs size={14} aria-hidden="true" />
                  Sessão
                </span>
                <h2>Dados do atendimento</h2>
                <p>
                  Confira o agendamento, informe o resultado da sessão e registre
                  o resumo clínico que será preservado no histórico do paciente.
                </p>
              </div>

              <div className="sc-score">
                <small>
                  <CalendarCheck2 size={13} aria-hidden="true" />
                  Completude
                </small>
                <strong>{completion}%</strong>
                <div className="sc-score__bar">
                  <span style={{ width: `${completion}%` }} />
                </div>
              </div>
            </div>

            <div className="sc-summary-grid">
              <article className="sc-summary-item">
                <span>Cliente</span>
                <strong>{cliente?.nome || "Cliente"}</strong>
              </article>

              <article className="sc-summary-item">
                <span>Data da sessão</span>
                <strong>{formatDateTimeBR(createLocalISO(dataSessao, horaSessao))}</strong>
              </article>

              <article className="sc-summary-item">
                <span>Tipo</span>
                <strong className="sc-summary-item__with-icon">
                  <SessionModeIcon size={15} aria-hidden="true" />
                  {sessionMode.label}
                </strong>
              </article>

              <article className="sc-summary-item">
                <span>Valor</span>
                <strong>{formatMoneyBR(valor)}</strong>
              </article>
            </div>

            <div
              className={`sc-google-status sc-google-status--${googleCalendarInfo.status}`}
              role="status"
            >
              <span className="sc-google-status__icon" aria-hidden="true">
                <GoogleStatusIcon size={18} />
              </span>

              <div className="sc-google-status__content">
                <strong>{googleCalendarInfo.title}</strong>
                <span>{googleCalendarInfo.description}</span>
              </div>

              {googleCalendarInfo.linked ? (
                <span className="sc-google-status__badge">Sincronização ativa</span>
              ) : null}
            </div>
          </section>

          <section className="sc-card">
            <div className="sc-card__head sc-card__head--compact">
              <div>
                <span className="sc-card__eyebrow">
                  <ClipboardCheck size={14} aria-hidden="true" />
                  Registro
                </span>
                <h2>Finalização</h2>
                <p>Preencha o status, duração, pagamento e resumo da sessão.</p>
              </div>
            </div>

            <div className="sc-status-grid">
              {SESSION_STATUS_OPTIONS.map((option) => (
                <StatusOptionCard
                  key={option.value}
                  option={option}
                  active={status === option.value}
                  disabled={saving || isBaixado}
                  onSelect={(value) => {
                    setStatus(value);
                    setErrors((prev) => ({ ...prev, status: "" }));
                  }}
                />
              ))}
            </div>

            {errors.status ? <div className="sc-field__err">{errors.status}</div> : null}

            <div className="sc-form-grid">
              <Field
                label="Data da sessão"
                required
                error={errors.dataSessao}
                hint="Pode ser corrigida"
              >
                <input
                  className="sc-input"
                  type="date"
                  value={dataSessao}
                  onChange={(event) => {
                    setDataSessao(event.target.value);
                    setErrors((prev) => ({ ...prev, dataSessao: "" }));
                  }}
                  disabled={saving || isBaixado}
                />
              </Field>

              <Field
                label="Horário da sessão"
                required
                error={errors.dataSessao}
                hint="Horário efetivo"
              >
                <input
                  className="sc-input"
                  type="time"
                  value={horaSessao}
                  onChange={(event) => {
                    setHoraSessao(event.target.value);
                    setErrors((prev) => ({ ...prev, dataSessao: "" }));
                  }}
                  disabled={saving || isBaixado}
                />
              </Field>

              <Field label="Duração" required error={errors.duracao} hint="minutos">
                <input
                  className="sc-input"
                  value={duracao}
                  onChange={(event) => setDuracaoSafe(event.target.value)}
                  placeholder="50"
                  inputMode="numeric"
                  disabled={saving || isBaixado}
                />
              </Field>

              <Field label="Valor" error={errors.valor} hint="R$">
                <input
                  className="sc-input"
                  value={valor}
                  onChange={(event) => setValorSafe(event.target.value)}
                  placeholder="0,00"
                  inputMode="numeric"
                  disabled={saving || isBaixado}
                />
              </Field>

              <div className="sc-form-grid__full">
                <PaymentSwitch
                  checked={pago}
                  onChange={setPago}
                  disabled={saving || isBaixado}
                />
              </div>

              <Field
                label="Resumo clínico da sessão"
                required
                error={errors.resumo}
                hint={`${resumoWords} palavra(s)`}
                className="sc-form-grid__full"
              >
                <div className="sc-prontuario-hint">
                  <NotebookTabs size={17} aria-hidden="true" />
                  <div>
                    <strong>Este registro alimenta o prontuário</strong>
                    <span>
                      Ao concluir a sessão, o resumo será incluído automaticamente
                      como uma nova evolução. Depois você poderá complementar
                      observações clínicas e recomendações sem alterar o resumo original.
                    </span>
                  </div>
                </div>

                <textarea
                  className="sc-textarea"
                  value={resumo}
                  onChange={(event) => setResumoSafe(event.target.value)}
                  onBlur={(event) =>
                    setResumoSafe(normalizeMultiline(event.target.value))
                  }
                  rows={7}
                  placeholder="Registre uma síntese clínica do atendimento, pontos trabalhados, evolução percebida e combinados relevantes..."
                  disabled={saving || isBaixado}
                />
              </Field>

              <div className="sc-form-grid__full">
                <section className="sc-ai-card">
                  <div className="sc-ai-card__head">
                    <div>
                      <span>
                        <BrainCircuit size={14} aria-hidden="true" />
                        PSYA Premium
                      </span>
                      <strong>Insight da sessão</strong>
                      <small>
                        Gere uma leitura de apoio com base no resumo registrado.
                      </small>
                    </div>

                    {isPremium ? (
                      <button
                        className="sc-ai-btn"
                        type="button"
                        onClick={gerarInsightSessao}
                        disabled={saving || isBaixado || insightLoading || !canGenerateInsight}
                      >
                        {insightLoading ? (
                          <>
                            <LoaderCircle size={15} aria-hidden="true" className="sc-icon-spin" />
                            Gerando...
                          </>
                        ) : (
                          <>
                            <Sparkles size={15} aria-hidden="true" />
                            Gerar insight
                          </>
                        )}
                      </button>
                    ) : null}
                  </div>

                  {!isPremium ? (
                    <div className="sc-ai-card__locked">
                      <div><LockKeyhole size={24} aria-hidden="true" /></div>
                      <strong>Recurso Premium</strong>
                      <span>
                        Atualize seu plano para gerar insights com IA sobre sessões.
                      </span>

                      <button
                        className="sc-btn sc-btn--primary"
                        type="button"
                        onClick={() => navigate("/plan-management")}
                        disabled={saving}
                      >
                        Ver planos
                      </button>
                    </div>
                  ) : insightLoading ? (
                    <div className="sc-ai-card__loading">
                      <span className="sc-spinner sc-spinner--dark" aria-hidden="true" />
                      Gerando insight da sessão...
                    </div>
                  ) : insightError ? (
                    <div className="sc-ai-card__error">{insightError}</div>
                  ) : sessionInsight ? (
                    <div className="sc-ai-card__result">
                      <div className="sc-ai-card__result-icon"><Sparkles size={20} aria-hidden="true" /></div>
                      <p>{sessionInsight}</p>
                    </div>
                  ) : (
                    <div className="sc-ai-card__empty">
                      <div><BrainCircuit size={25} aria-hidden="true" /></div>
                      <strong>Pronto para gerar</strong>
                      <span>
                        Após preencher o resumo, clique em “Gerar insight”.
                        O texto é apenas apoio ao psicólogo e não substitui avaliação profissional.
                      </span>
                    </div>
                  )}
                </section>
              </div>
            </div>

            <div className="sc-actions">
              <button
                className="sc-btn sc-btn--ghost"
                type="button"
                onClick={voltar}
                disabled={saving}
              >
                <ArrowLeft size={16} aria-hidden="true" />
                Voltar
              </button>

              <button
                className="sc-btn sc-btn--primary"
                type="button"
                onClick={salvarSessao}
                disabled={saving || isBaixado}
                title={isBaixado ? "Agendamento já encerrado" : "Registrar sessão"}
              >
                {saving ? (
                  <>
                    <span className="sc-spinner" aria-hidden="true" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save size={17} aria-hidden="true" />
                    Registrar sessão
                  </>
                )}
              </button>
            </div>
          </section>
        </section>
      </main>
    </div>
  );
}
